import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MergeCandidateSession } from "@/types/testSessionTypes";
import { toggleSelection } from "@utils/testSessionMergeUtils";

interface TestSessionListState {
  data: any[];
  loading: boolean;
  error: string | null;
  lastPage: number | null;
  page: number;
  filters: {
    testSuiteNames?: string[];
  };
  mergeMode: boolean;
  mergeSelection: MergeCandidateSession[];
  merging: boolean;
  mergeError: string | null;
  mergeCompletedCount: number;
  mergedSessionId: string | null;
}

const initialState: TestSessionListState = {
  data: [],
  loading: false,
  error: null,
  lastPage: null,
  page: 1,
  filters: {},
  mergeMode: false,
  mergeSelection: [],
  merging: false,
  mergeError: null,
  mergeCompletedCount: 0,
  mergedSessionId: null,
};

const testSessionListSlice = createSlice({
  name: "testSessionList",
  initialState,
  reducers: {
    fetchTestSessionListRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
      if (action.payload && action.payload.page) {
        state.page = action.payload.page;
      }
    },
    fetchTestSessionListSuccess(
      state,
      action: PayloadAction<{ data: any[]; lastPage: number | null; filters?: any }>,
    ) {
      state.data = action.payload.data;
      state.lastPage = action.payload.lastPage;
      state.loading = false;
      state.error = null;
      if (action.payload.filters) {
        state.filters = { ...action.payload.filters };
      }
    },
    fetchTestSessionListFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    clearTestSessionList(state) {
      state.data = [];
      state.loading = false;
      state.error = null;
      state.lastPage = null;
      state.page = 1;
      state.filters = {};
      state.mergeMode = false;
      state.mergeSelection = [];
      state.merging = false;
      state.mergeError = null;
      state.mergedSessionId = null;
    },
    setTestSessionListPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setTestSessionListFilters(state, action: PayloadAction<any>) {
      state.filters = action.payload;
    },
    startSessionMerge(state) {
      state.mergeMode = true;
      state.mergeSelection = [];
      state.mergeError = null;
      state.mergedSessionId = null;
    },
    cancelSessionMerge(state) {
      state.mergeMode = false;
      state.mergeSelection = [];
      state.merging = false;
      state.mergeError = null;
    },
    toggleSessionMergeSelection(state, action: PayloadAction<MergeCandidateSession>) {
      state.mergeSelection = toggleSelection(state.mergeSelection, action.payload);
      state.mergeError = null;
    },
    mergeTestSessionsRequest(state, _action: PayloadAction<{ patientId: string }>) {
      state.merging = true;
      state.mergeError = null;
    },
    mergeTestSessionsSuccess(state, action: PayloadAction<{ sessionId: string | null }>) {
      state.merging = false;
      state.mergeMode = false;
      state.mergeSelection = [];
      state.mergeError = null;
      state.mergeCompletedCount += 1;
      state.mergedSessionId = action.payload.sessionId;
    },
    mergeTestSessionsFailure(state, action: PayloadAction<string>) {
      state.merging = false;
      state.mergeError = action.payload;
    },
  },
});

export const {
  fetchTestSessionListRequest,
  fetchTestSessionListSuccess,
  fetchTestSessionListFailure,
  clearTestSessionList,
  setTestSessionListPage,
  setTestSessionListFilters,
  startSessionMerge,
  cancelSessionMerge,
  toggleSessionMergeSelection,
  mergeTestSessionsRequest,
  mergeTestSessionsSuccess,
  mergeTestSessionsFailure,
} = testSessionListSlice.actions;

export default testSessionListSlice.reducer;
